import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { notesApiService } from '../services/notesApi';
import { X, Loader, CheckCircle, UploadCloud, AlertCircle, FileText, Link2, LucideIcon, Search, User, TriangleAlert, Axe, Paperclip, File, Lock } from 'lucide-react';
import { Evidence, AssociatedItem } from '../types/player';
import { cn } from '../utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { PlayerProfile } from './PlayerProfile';
import { CreatorProfile } from './CreatorProfile';
import { ConfidentialEvidencePlaceholder } from './ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from './ConfidentialBadge';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { useAuth } from '../contexts/AuthContext';
import { EmptyState } from './ui/EmptyState';
import { SkeletonLoader } from './SkeletonLoader';

interface AddEvidenceModalProps {
  itemId: string;
  itemType: 'note' | 'warning' | 'ban';
  onClose: () => void;
  onSuccess: (newEvidence?: Evidence) => void;
  initialTab?: Tab;
}

type Tab = 'upload' | 'text' | 'link' | 'link_evidence';

export const AddEvidenceModal = ({ itemId, itemType, onClose, onSuccess, initialTab = 'upload' }: AddEvidenceModalProps) => {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [files, setFiles] = useState<File[]>([]);
  const [textContent, setTextContent] = useState('');
  const [isConfidential, setIsConfidential] = useState(false);
  const [targetItemId, setTargetItemId] = useState('');
  const [recentItems, setRecentItems] = useState<AssociatedItem[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilters, setTypeFilters] = useState<Array<'note' | 'warning' | 'ban'>>(['note', 'warning', 'ban']);

  // Link Evidence tab state
  const [allEvidence, setAllEvidence] = useState<Evidence[]>([]);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
  const [evidenceSearchQuery, setEvidenceSearchQuery] = useState('');
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const modalRef = useRef<HTMLFormElement>(null);
  useOnClickOutside(modalRef, onClose);
  const { identity } = useAuth();

  useEffect(() => {
    if (activeTab === 'link') {
      const fetchRecentItems = async () => {
        setIsLoadingRecent(true);
        try {
          const items = await notesApiService.getRecentItems();
          setRecentItems(items);
        } catch (err) {
          setError('Failed to load recent items.');
        } finally {
          setIsLoadingRecent(false);
        }
      };
      fetchRecentItems();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'link_evidence') {
      const fetchAllEvidence = async () => {
        setIsLoadingEvidence(true);
        try {
          const data = await notesApiService.getAllEvidence();
          setAllEvidence(data);
        } catch (err) {
          setError('Failed to load evidence.');
        } finally {
          setIsLoadingEvidence(false);
        }
      };
      fetchAllEvidence();
    }
  }, [activeTab]);

  const filteredLinkEvidence = useMemo(() => {
    if (!evidenceSearchQuery.trim()) return allEvidence;
    const query = evidenceSearchQuery.toLowerCase();
    return allEvidence.filter(e =>
      e.filename.toLowerCase().includes(query) ||
      e.uploader_id.toLowerCase().includes(query) ||
      (e.item_id && e.item_id.toLowerCase().includes(query))
    );
  }, [allEvidence, evidenceSearchQuery]);

  const handleToggleFilter = (type: 'note' | 'warning' | 'ban') => {
    setTypeFilters(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setIsSuccess(false);

    try {
      let newEvidence: Evidence | null = null;
      if (activeTab === 'upload') {
        if (files.length === 0) {
          setError("Please select at least one file to upload.");
          setIsSubmitting(false);
          return;
        }

        // Use bulk upload if multiple files, single upload for one file
        if (files.length === 1) {
          newEvidence = await notesApiService.uploadEvidence(files[0], itemId, itemType, isConfidential || undefined);
        } else {
          const result = await notesApiService.uploadMultipleEvidence(files, itemId, itemType, isConfidential || undefined);

          if (result.failedCount > 0) {
            const failedNames = result.failed.map(f => f.filename).join(', ');
            if (result.successCount === 0) {
              // All files failed
              setError(`Failed to upload files: ${failedNames}`);
              setIsSubmitting(false);
              return;
            } else {
              // Some files failed, but some succeeded
              setError(`Warning: Some files failed to upload: ${failedNames}. ${result.successCount} file(s) uploaded successfully.`);
            }
          }

          // Return the first uploaded evidence for the success callback
          // Note: The parent component should refresh to see all uploaded evidence
          newEvidence = result.uploaded[0] || null;
        }
      } else if (activeTab === 'text') {
        if (!textContent.trim()) {
          setError("Please enter some text evidence.");
          setIsSubmitting(false);
          return;
        }
        newEvidence = await notesApiService.addTextEvidence(itemId, itemType, textContent, isConfidential || undefined);
      } else if (activeTab === 'link') {
        if (!targetItemId.trim()) {
            setError("Please enter an Item ID to link.");
            setIsSubmitting(false);
            return;
        }
        await notesApiService.linkItems(itemId, targetItemId);
        // Note: linkItems doesn't return evidence, so we can't add it to the list directly.
        // We will just close the modal on success. A page refresh would show the link.
      } else if (activeTab === 'link_evidence') {
        if (!selectedEvidenceId) {
            setError("Please select an existing piece of evidence to link.");
            setIsSubmitting(false);
            return;
        }
        await notesApiService.linkEvidence(selectedEvidenceId, itemId, itemType);
      }

      setIsSuccess(true);
      if (newEvidence) {
          onSuccess(newEvidence);
      } else {
          onSuccess();
      }
      setTimeout(onClose, 1500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const TABS: { id: Tab, label: string, icon: LucideIcon }[] = [
    { id: 'upload', label: 'Upload File', icon: UploadCloud },
    { id: 'text', label: 'Add Text', icon: FileText },
    { id: 'link', label: 'Link Item', icon: Link2 },
    { id: 'link_evidence', label: 'Link Evidence', icon: Paperclip },
  ];

  const ActiveIcon = TABS.find(t => t.id === activeTab)?.icon;

  const confidentialCheckbox = (
    <label className="flex items-center gap-2 mt-3 cursor-pointer select-none group/conf">
      <input
        type="checkbox"
        checked={isConfidential}
        onChange={(e) => setIsConfidential(e.target.checked)}
        className="form-checkbox text-amber-500 focus:ring-amber-500"
      />
      <Lock className={cn("w-3.5 h-3.5", isConfidential ? "text-amber-400" : "text-glass-400 group-hover/conf:text-amber-400/60")} />
      <span className={cn("text-sm", isConfidential ? "text-amber-300 font-medium" : "text-glass-400")}>
        Mark as Confidential
      </span>
    </label>
  );

  const modalContent = (
    <div className="modal-overlay z-[99999]" style={{ zIndex: 99999 }}>
      <form
        ref={modalRef}
        onSubmit={handleSubmit}
        className={cn("modal-panel max-h-[90vh] min-h-[400px]", activeTab === 'link_evidence' ? 'max-w-lg' : 'max-w-md')}
        style={{ zIndex: 99999 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-evidence-title"
      >
        <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close"><X className="w-4 h-4" /></button>

        <div className="flex items-start gap-3 mb-4 pr-8">
            <span className="icon-tile icon-tile-accent">
                {ActiveIcon && <ActiveIcon className="w-4 h-4" />}
            </span>
            <div className="min-w-0">
                <h2 id="add-evidence-title" className="modal-title">Add Evidence</h2>
                <p className="modal-subtitle">Link evidence to this {itemType}.</p>
            </div>
        </div>

        <nav className="tabs flex-wrap max-w-full mb-4" role="tablist" aria-label="Add evidence sections">
            {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        data-active={tab.id === activeTab ? 'true' : 'false'}
                        aria-selected={tab.id === activeTab}
                        role="tab"
                        className="tab touch-manipulation"
                    >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                );
            })}
        </nav>

        <div className="modal-body">
            {activeTab === 'upload' && (
                <div>
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">Evidence Files</label>
                    <input
                        type="file"
                        onChange={handleFileChange}
                        className="form-input w-full touch-manipulation"
                        accept="image/*,video/*,.pdf,.txt,.doc,.docx"
                        multiple
                    />
                    {files.length > 0 && (
                        <div className="mt-2">
                            <p className="text-xs text-green-400">
                                Selected: {files.length} file{files.length > 1 ? 's' : ''}
                            </p>
                            <ul className="subcard mt-1.5 p-2 text-xs text-glass-400 max-h-32 overflow-y-auto space-y-0.5">
                                {files.map((file, index) => (
                                    <li key={index} className="truncate">• {file.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {confidentialCheckbox}
                </div>
            )}
            {activeTab === 'text' && (
                <div>
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">Evidence Content</label>
                    <textarea
                        value={textContent}
                        onChange={(e) => setTextContent(e.target.value)}
                        className="glass-input w-full min-h-[100px] resize-y"
                        rows={4}
                        placeholder="Paste or type text evidence here..."
                    />
                    {confidentialCheckbox}
                </div>
            )}
            {activeTab === 'link' && (
                <div className="flex flex-col h-full">
                    <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="glass-input w-full pl-9"
                            placeholder="Search by content, creator, or user UUID..."
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <span className="eyebrow mr-1">Filter</span>
                        <button type="button" onClick={() => handleToggleFilter('note')} aria-pressed={typeFilters.includes('note')} className={cn("badge", typeFilters.includes('note') ? 'badge-info' : 'badge-neutral opacity-60')}>
                            <FileText className="w-3 h-3" /> <span>Notes</span>
                        </button>
                        <button type="button" onClick={() => handleToggleFilter('warning')} aria-pressed={typeFilters.includes('warning')} className={cn("badge", typeFilters.includes('warning') ? 'badge-warning' : 'badge-neutral opacity-60')}>
                            <TriangleAlert className="w-3 h-3" /> <span>Warnings</span>
                        </button>
                        <button type="button" onClick={() => handleToggleFilter('ban')} aria-pressed={typeFilters.includes('ban')} className={cn("badge", typeFilters.includes('ban') ? 'badge-danger' : 'badge-neutral opacity-60')}>
                            <Axe className="w-3 h-3" /> <span>Bans</span>
                        </button>
                    </div>
                    {isLoadingRecent ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" aria-busy="true">
                            {[0, 1, 2, 3].map((i) => <SkeletonLoader key={i} className="h-20 w-full rounded-lg" />)}
                        </div>
                    ) : (
                        <div className="flex-grow overflow-y-auto -mr-2 pr-2 max-h-[200px] grid grid-cols-1 sm:grid-cols-2 gap-2 content-start">
                            {recentItems.filter(item => {
                                const searchMatch = (item.content || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    (item.creator || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    (item.user_uuid || '').toLowerCase().includes(searchQuery.toLowerCase());
                                const typeMatch = typeFilters.includes(item.type);
                                return searchMatch && typeMatch;
                            }).map(item => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setTargetItemId(item.id)}
                                    aria-pressed={targetItemId === item.id}
                                    className={cn(
                                        "w-full text-left subcard p-3 transition-colors",
                                        targetItemId === item.id ? 'border-accent-400/60 bg-accent-500/10' : 'hover:border-glass-200/25'
                                    )}
                                >
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={cn("badge capitalize",
                                                item.type === 'ban' && 'badge-danger',
                                                item.type === 'warning' && 'badge-warning',
                                                item.type === 'note' && 'badge-info'
                                            )}>{item.type}</span>
                                            <PlayerProfile uuid={item.user_uuid} inline />
                                        </div>
                                        <span className="text-[11px] text-glass-400 tabular-nums whitespace-nowrap">{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
                                    </div>
                                    <p className="text-sm text-white truncate mb-1.5">{item.content}</p>
                                    <div className="text-xs text-glass-400 flex items-center gap-1 min-w-0">
                                        <User className="w-3 h-3 shrink-0" />
                                        <span className="truncate">by {item.creator}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    <input
                      type="text"
                      value={targetItemId}
                      onChange={(e) => setTargetItemId(e.target.value)}
                      className="glass-input w-full mt-2 font-mono text-xs"
                      placeholder="Or paste an Item ID directly..."
                      aria-label="Item ID"
                    />
                </div>
            )}
            {activeTab === 'link_evidence' && (
                <div className="flex flex-col h-full">
                    <p className="text-sm text-glass-400 mb-3">
                        Select existing evidence to link to this {itemType}. Useful for sharing evidence across multiple items.
                    </p>
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                        <input
                            type="text"
                            value={evidenceSearchQuery}
                            onChange={(e) => setEvidenceSearchQuery(e.target.value)}
                            className="glass-input w-full pl-9"
                            placeholder="Search by filename, uploader, or item ID..."
                        />
                    </div>
                    {isLoadingEvidence ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" aria-busy="true">
                            {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonLoader key={i} className="aspect-square w-full rounded-lg" />)}
                        </div>
                    ) : filteredLinkEvidence.length === 0 ? (
                        <EmptyState
                            bare
                            compact
                            icon={Paperclip}
                            title={evidenceSearchQuery ? 'No evidence matches your search.' : 'No evidence available.'}
                        />
                    ) : (
                        <div className="flex-grow overflow-y-auto -mr-2 pr-2 max-h-[280px] grid grid-cols-2 sm:grid-cols-3 gap-2 content-start">
                            {filteredLinkEvidence.map((ev) => {
                                const redacted = isEvidenceRedacted(ev, identity);
                                return (
                                <button
                                    key={`${ev.id}-${ev.item_id}`}
                                    type="button"
                                    onClick={() => setSelectedEvidenceId(ev.id)}
                                    aria-pressed={selectedEvidenceId === ev.id}
                                    className={cn(
                                        "text-left rounded-lg overflow-hidden border transition-colors",
                                        selectedEvidenceId === ev.id
                                            ? 'border-accent-400/60 bg-accent-500/10'
                                            : 'border-glass-200/10 bg-glass-100/5 hover:border-glass-200/25'
                                    )}
                                >
                                    <div className="aspect-square bg-dark-900/40 overflow-hidden">
                                        {redacted ? (
                                            <ConfidentialEvidencePlaceholder compact />
                                        ) : ev.content_type.startsWith('image/') ? (
                                            <img
                                                src={getEvidenceViewUrl(ev.id)}
                                                alt={ev.filename}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center text-glass-400 p-2">
                                                <File className="w-7 h-7" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-2">
                                        <div className="flex items-center gap-1">
                                            <p className="text-xs font-medium text-white truncate flex-1" title={ev.filename}>
                                                {ev.filename}
                                            </p>
                                            {ev.confidential && !redacted && <ConfidentialBadge className="ml-1" />}
                                        </div>
                                        <div className="text-xs text-glass-400 truncate">
                                            <CreatorProfile creatorId={ev.uploader_id} inline />
                                        </div>
                                        <p className="text-[11px] text-glass-400 tabular-nums">
                                            {formatDistanceToNow(new Date(ev.created_at), { addSuffix: true })}
                                        </p>
                                    </div>
                                </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>

        {error && (
            <div className="alert alert-danger mt-4" role="alert">
                <AlertCircle /><span>{error}</span>
            </div>
        )}
        <div className="modal-footer">
            <button type="button" onClick={onClose} className="glass-button-secondary w-full sm:w-auto touch-manipulation">Cancel</button>
            <button type="submit" disabled={isSubmitting || isSuccess} className="btn-primary w-full sm:w-auto touch-manipulation">
                {isSubmitting && <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>}
                {isSuccess && <><CheckCircle className="w-4 h-4" /> Success!</>}
                {!isSubmitting && !isSuccess && `Submit ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`}
            </button>
        </div>
      </form>
    </div>
  );

  return createPortal(modalContent, document.body);
};
